import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/** "Gray" and "Grey" are the same colour; compare in lowercase. */
const normaliseColour = (name) => (name || '').trim().toLowerCase().replace('gray', 'grey');

/** The colour an image's alt text names, e.g. "… trunk in Maroon — side view". */
const colourFromAlt = (alt) => normaliseColour(/ in (.+?) — /.exec(alt || '')?.[1]);

/** The shot an image's alt text names, e.g. "… — styled with a blazer (…)". */
const viewFromAlt = (alt) => (/ — ([^(]+)/.exec(alt || '')?.[1] || '').trim().toLowerCase();

/** With a curated gallery: each pose once, the colours taking turns, blazer first. */
const POSES = [
  'styled with a blazer',
  'front view on model',
  'back view on model',
  'side view',
  'running, front view',
  'running, back view',
];
const PRODUCT_FRONT = ['front view', 'front-product view'];

/**
 * <custom-combo-builder>
 *
 * Build-your-own pack builder.
 *
 * Rather than selling a placeholder bundle product, this adds the chosen number
 * of real single-trunk variants to the cart. That means stock is drawn from the
 * exact colour and size the shopper picked, the warehouse packs against real
 * SKUs, and nothing can be oversold. The pack price is produced by an automatic
 * quantity discount on the single trunk.
 *
 * If the script fails, the page still has a working native form for the pack
 * product with the chosen combo recorded as line-item properties, so an order
 * is never silently lost — it just doesn't draw down the singles.
 */
class CustomComboBuilder extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onSwatchClick);
    this.addEventListener('change', this.#onSizeChange);
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#detachSubmit();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  #init() {
    // Gallery thumbnails call showSlide() on this element by id (see #syncGallery).
    if (!this.id) this.id = 'combo-builder';
    this.variants = this.#readVariantMap();
    this.#attachSubmit();
    this.#sync();
    this.#syncGallery();
  }

  #readVariantMap() {
    const node = this.querySelector('script[data-variant-map]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[combo-builder] bad variant map', error);
      return {};
    }
  }

  get #form() {
    const scope = this.closest('.shopify-section') || document;
    return scope.querySelector('form[action*="/cart/add"]');
  }

  #attachSubmit() {
    this.#detachSubmit();
    const form = this.#form;
    if (!form) return;
    this.boundForm = form;

    // The theme's add-to-cart button runs its own click handler rather than
    // submitting the form, so intercept the click on the document in capture
    // phase — that runs before any handler bound further down the tree. The
    // submit listener is a belt-and-braces fallback.
    document.addEventListener('click', this.#onAddToCartClick, true);
    form.addEventListener('submit', this.#onSubmit, true);
  }

  #detachSubmit() {
    document.removeEventListener('click', this.#onAddToCartClick, true);
    this.boundForm?.removeEventListener('submit', this.#onSubmit, true);
    this.boundForm = null;
  }

  #onAddToCartClick = (event) => {
    const button = event.target.closest?.(
      '[ref="addToCartButton"], .add-to-cart-button, button[type="submit"], button[name="add"]'
    );
    if (!button || !this.boundForm?.contains(button)) return;
    this.#onSubmit(event);
  };

  #onSwatchClick = (event) => {
    const swatch = event.target.closest('[data-combo-swatch]');
    if (!swatch || !this.contains(swatch)) return;
    event.preventDefault();
    if (swatch.dataset.unavailable === 'true') return;

    const row = swatch.closest('[data-piece]');
    if (!row) return;
    row.querySelectorAll('[data-combo-swatch]').forEach((s) => s.setAttribute('aria-checked', 'false'));
    swatch.setAttribute('aria-checked', 'true');
    this.#sync();
    this.#syncGallery(swatch.dataset.colourName);
  };

  #onSizeChange = (event) => {
    if (!event.target.matches('[data-combo-size]')) return;
    this.#sync();
  };

  /** Current selection for each row. */
  get selection() {
    return Array.from(this.querySelectorAll('[data-piece]')).map((row) => {
      // Only a colour the shopper actually picked counts (a pack can start with none).
      const swatch = row.querySelector('[data-combo-swatch][aria-checked="true"]');
      const select = row.querySelector('[data-combo-size]');
      return {
        row,
        colour: swatch?.dataset.colourName ?? '',
        size: select?.value ?? '',
      };
    });
  }

  /**
   * Reflect availability on the swatches and keep the no-JS fallback
   * properties in step with what's on screen.
   */
  #sync() {
    this.selection.forEach(({ row, colour, size }) => {
      row.querySelectorAll('[data-combo-swatch]').forEach((swatch) => {
        const entry = this.variants[`${size}|${swatch.dataset.colourName}`];
        swatch.dataset.unavailable = entry && entry.available ? 'false' : 'true';
      });

      const input = row.querySelector('input[data-combo-prop]');
      if (input) input.value = `${colour || 'Not chosen'} / ${size}`;

      // Large-swatch layout names the picked colour next to "Trunk N".
      const picked = row.querySelector('[data-picked-name]');
      if (picked) picked.textContent = colour ? ` · ${colour}` : '';
    });

    this.#setError('');
  }

  /**
   * Keep the product gallery to the colours in this pack, in trunk order, and
   * move it to the colour just picked.
   *
   * Images are tied to a colour by their alt text. Slides and thumbnails for
   * picked colours go first, in trunk order, with the rest hidden after them:
   * the slideshow only counts visible slides and its thumbnails select by
   * position, so keeping the visible ones first keeps the two in step. The
   * zoom dialog is opened by each slide's original index, so it keeps its
   * order and only hides the colours that aren't in the pack.
   *
   * Until a colour is picked (a pack can start with none) the whole gallery
   * shows, led by the pack's hero image (data-hero-media). The first pick
   * hides the hero.
   *
   * @param {string} [focusColour] - The colour just picked.
   */
  #syncGallery(focusColour, attempt = 0) {
    const scope = this.closest('.shopify-section') || document;
    const slideshow = scope.querySelector('slideshow-component');
    const scroller = slideshow?.querySelector('[ref="scroller"]');
    if (!slideshow || !scroller) return;

    // The slideshow's animated jump (e.g. from the zoom dialog) parks a placeholder
    // slide and puts the real one back when its scroll ends. Reordering in between
    // drops that slide in the wrong place, so wait for it to finish.
    if (
      scroller.hasAttribute('data-combo-tagged') &&
      scroller.querySelector(':scope > slideshow-slide:not([data-combo-index])') &&
      attempt < 20
    ) {
      setTimeout(() => this.#syncGallery(focusColour, attempt + 1), 100);
      return;
    }

    const slides = Array.from(scroller.querySelectorAll(':scope > slideshow-slide'));
    const thumbs = Array.from(slideshow.querySelectorAll('[ref="thumbnails[]"]'));
    if (!slides.length) return;

    const heroId = this.dataset.heroMedia;
    const holdsHero = (el) => Boolean(heroId) && el.querySelector('[data-media-id]')?.dataset.mediaId === heroId;

    // Record each slide's original position and colour once, since the
    // reordering below means neither can be read back from the DOM later.
    if (!scroller.hasAttribute('data-combo-tagged')) {
      slides.forEach((slide, index) => {
        slide.dataset.comboIndex = String(index);
        slide.dataset.comboColour = colourFromAlt(slide.querySelector('img')?.alt);
        slide.dataset.comboView = viewFromAlt(slide.querySelector('img')?.alt);
        slide.toggleAttribute('data-combo-hero', holdsHero(slide));
        if (thumbs[index]) {
          thumbs[index].dataset.comboIndex = String(index);
          thumbs[index].dataset.comboColour = slide.dataset.comboColour;
          thumbs[index].dataset.comboView = slide.dataset.comboView;
          thumbs[index].toggleAttribute('data-combo-hero', slide.hasAttribute('data-combo-hero'));
        }
      });
      scroller.setAttribute('data-combo-tagged', '');
    }
    if (!slides.some((slide) => slide.dataset.comboColour)) return;

    const picked = [...new Set(this.selection.map(({ colour }) => normaliseColour(colour)).filter(Boolean))];
    const byIndex = (a, b) => Number(a.dataset.comboIndex) - Number(b.dataset.comboIndex);
    const isHero = (el) => el.hasAttribute('data-combo-hero');
    const curated = slideshow.closest('media-gallery')?.hasAttribute('data-curated');
    const arrange = (items) => {
      const sorted = [...items].sort(byIndex);
      if (curated) {
        // Each chosen colour's product shot, then one shot per pose with the colours
        // taking turns. Before any pick: the hero, then the same across every colour.
        const colours = picked.length
          ? picked
          : [...new Set(sorted.map((el) => el.dataset.comboColour).filter(Boolean))];
        const find = (colour, views) =>
          sorted.find((el) => el.dataset.comboColour === colour && views.includes(el.dataset.comboView));
        const shown = picked.length ? [] : sorted.filter(isHero);
        colours.forEach((colour) => {
          const front = find(colour, PRODUCT_FRONT);
          if (front) shown.push(front);
        });
        POSES.forEach((pose, turn) => {
          for (let step = 0; step < colours.length; step++) {
            const el = find(colours[(turn + step) % colours.length], [pose]);
            if (el) {
              shown.push(el);
              break;
            }
          }
        });
        return { shown, hidden: sorted.filter((el) => !shown.includes(el)) };
      }
      // Nothing picked yet: every image, the hero first.
      if (!picked.length) return { shown: [...sorted.filter(isHero), ...sorted.filter((el) => !isHero(el))], hidden: [] };
      const shown = picked.flatMap((colour) => sorted.filter((el) => el.dataset.comboColour === colour));
      const uncoloured = sorted.filter((el) => !el.dataset.comboColour && !isHero(el));
      const hidden = sorted.filter(
        (el) => isHero(el) || (el.dataset.comboColour && !picked.includes(el.dataset.comboColour))
      );
      return { shown: [...shown, ...uncoloured], hidden };
    };
    const place = (parent, { shown, hidden }) => {
      const wanted = [...shown, ...hidden];
      const current = Array.from(parent.children).filter((el) => wanted.includes(el));
      if (wanted.some((el, index) => current[index] !== el)) parent.append(...wanted);
      shown.forEach((el) => el.removeAttribute('data-combo-hidden'));
      hidden.forEach((el) => el.setAttribute('data-combo-hidden', ''));
    };

    const slidePlan = arrange(slides);
    this.#withoutSnap(scroller, () => place(scroller, slidePlan));
    slidePlan.shown.forEach((slide) => slide.removeAttribute('hidden'));
    slidePlan.hidden.forEach((slide) => {
      slide.setAttribute('hidden', '');
      slide.setAttribute('aria-hidden', 'true');
    });

    if (thumbs.length && thumbs[0].parentElement) {
      const thumbPlan = arrange(thumbs);
      place(thumbs[0].parentElement, thumbPlan);
      thumbPlan.shown.forEach((thumb, index) => {
        thumb.setAttribute('on:click', `#${this.id}/showSlide/${thumb.dataset.comboIndex}`);
        thumb.setAttribute('aria-label', `Slide ${index + 1} of ${thumbPlan.shown.length}`);
      });
    }

    const zoom = scope.querySelector('zoom-dialog');
    if (zoom) {
      // The zoom list and its thumbnails come in the same order.
      const zoomThumbs = Array.from(zoom.querySelectorAll('[ref="thumbnails"] button'));
      // Zoom items follow the slides' original order, so hide whatever the slides hide.
      const hiddenIndexes = new Set(slidePlan.hidden.map((slide) => Number(slide.dataset.comboIndex)));
      Array.from(zoom.querySelectorAll('[ref="media[]"]')).forEach((el, index) => {
        const hide = hiddenIndexes.has(index);
        el.toggleAttribute('data-combo-hidden', hide);
        zoomThumbs[index]?.toggleAttribute('data-combo-hidden', hide);
      });
    }

    const target = focusColour
      ? Math.max(0, slidePlan.shown.findIndex((slide) => slide.dataset.comboColour === normaliseColour(focusColour)))
      : 0;
    const targetSlide = slidePlan.shown[target];
    customElements.whenDefined('slideshow-component').then(() => {
      if (targetSlide) this.#withoutSnap(scroller, () => this.#jumpTo(slideshow, scroller, targetSlide));
    });
  }

  /**
   * Thumbnail clicks in this page's gallery land here instead of on the slideshow
   * (see #syncGallery). The slideshow's own thumbnail jump is animated by moving
   * slides around, which clashes with the colour ordering and left pictures in the
   * wrong place.
   *
   * @param {number} comboIndex - The slide's original position (data-combo-index).
   * @param {Event} [event]
   */
  showSlide(comboIndex, event) {
    event?.preventDefault();
    const slideshow = (this.closest('.shopify-section') || document).querySelector('slideshow-component');
    const scroller = slideshow?.querySelector('[ref="scroller"]');
    const slide = scroller?.querySelector(`:scope > slideshow-slide[data-combo-index="${comboIndex}"]`);
    if (slideshow && scroller && slide) this.#withoutSnap(scroller, () => this.#jumpTo(slideshow, scroller, slide));
  }

  /**
   * Put `slide` on screen at once and mark its thumbnail. Scrolls the gallery
   * strip itself rather than calling select(), which ignores requests while one
   * of its animations runs and can skip a slide it thinks is already showing.
   *
   * @param {HTMLElement} slideshow
   * @param {HTMLElement} scroller
   * @param {HTMLElement} slide
   */
  #jumpTo(slideshow, scroller, slide) {
    // The slideshow rebuilds its slide list on its own schedule; do it now so the
    // index below matches the order just set.
    slideshow.updatedCallback?.();
    const index = (slideshow.slides ?? []).indexOf(slide);
    const left = scroller.scrollLeft + slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
    scroller.scrollTo({ left, behavior: 'instant' });
    if (index >= 0 && 'current' in slideshow) slideshow.current = index;

    // Bring the matching thumbnail into view within its own strip only, so the
    // page itself never scrolls.
    const thumb = slideshow.querySelector(`[ref="thumbnails[]"][data-combo-index="${slide.dataset.comboIndex}"]`);
    let strip = thumb?.parentElement;
    while (strip && strip !== slideshow && strip.scrollWidth <= strip.clientWidth) strip = strip.parentElement;
    if (thumb && strip && strip !== slideshow) {
      const offset = thumb.getBoundingClientRect().left - strip.getBoundingClientRect().left;
      strip.scrollTo({ left: strip.scrollLeft + offset - (strip.clientWidth - thumb.clientWidth) / 2, behavior: 'instant' });
    }
  }

  /** @type {{ value: string } | null} */
  #snapRestore = null;

  #snapFrame = 0;

  /**
   * Run `change` with scroll snapping off. After slides are reordered the browser
   * re-snaps to whichever slide was on screen before, pulling the gallery back off
   * the picked colour; snapping comes back two frames later, once the new position
   * has settled.
   *
   * @param {HTMLElement} scroller
   * @param {() => void} change
   */
  #withoutSnap(scroller, change) {
    if (!this.#snapRestore) {
      this.#snapRestore = { value: scroller.style.scrollSnapType };
      scroller.style.scrollSnapType = 'none';
    }
    change();
    cancelAnimationFrame(this.#snapFrame);
    this.#snapFrame = requestAnimationFrame(() => {
      this.#snapFrame = requestAnimationFrame(() => {
        if (!this.#snapRestore) return;
        scroller.style.scrollSnapType = this.#snapRestore.value;
        this.#snapRestore = null;
      });
    });
  }

  #setError(message) {
    const el = this.querySelector('[data-combo-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  #onSubmit = async (event) => {
    const addButton = this.boundForm?.querySelector('[ref="addToCartButton"]');
    if (addButton) delete addButton.dataset.comboBlocked;

    // Every trunk needs a colour before the pack can go in the cart.
    const missing = this.selection.filter(({ colour }) => !colour);
    if (missing.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      // Tells the sticky bar the add was refused, so it doesn't play its fly-to-cart.
      if (addButton) addButton.dataset.comboBlocked = 'true';
      const first = missing[0].row;
      this.#setError(
        missing.length === 1 ? `Pick a colour for trunk ${first.dataset.piece}` : 'Pick a colour for each trunk'
      );
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first.querySelector('[data-combo-swatch]')?.focus({ preventScroll: true });
      return;
    }

    const items = [];
    const unavailable = [];
    const packId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

    for (const { colour, size } of this.selection) {
      const entry = this.variants[`${size}|${colour}`];
      if (!entry || !entry.available) {
        unavailable.push(`${colour} / ${size}`);
        continue;
      }
      items.push({
        id: entry.id,
        quantity: 1,
        properties: {
          // Underscore-prefixed, so these are hidden from the cart and checkout
          // but still reach the order in admin for the warehouse to pack against.
          // _PackId is unique per add, which is what lets the cart template group
          // this pack's lines under one heading and keeps two separate packs of
          // the same colours from merging into each other.
          _Pack: this.dataset.packLabel || 'Build your own',
          _PackId: packId,
        },
      });
    }

    // Nothing usable — let the native form proceed rather than block the sale.
    if (!items.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (unavailable.length) {
      this.#setError(`Sold out: ${unavailable.join(', ')}`);
      return;
    }

    const button = this.boundForm?.querySelector('[ref="addToCartButton"], button[type="submit"]');
    button?.setAttribute('disabled', '');

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || 'Could not add to cart');
      }

      // Let the theme refresh the cart drawer/bubble.
      document.dispatchEvent(new CartAddEvent({}, String(items[0].id), { didError: false }));
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, { bubbles: true, detail: { data: {} } }));
    } catch (error) {
      console.error('[combo-builder]', error);
      this.#setError(error.message || 'Could not add to cart');
    } finally {
      button?.removeAttribute('disabled');
    }
  };
}

if (!customElements.get('custom-combo-builder')) {
  customElements.define('custom-combo-builder', CustomComboBuilder);
}
